// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@conklin/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Change
    productListProductImage?: ProductListProductImageStyles;
    productInformationStyles?: ProductListProductInformationStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    // XNG-Change: restructure if its tablet (Price within the info, compare & show add to list from actions)
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListProductImage}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.infoGridItem}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={false}
                    showAttributes={props.showAttributes}
                    showImageBadges={false}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={false}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    {...{
                        extendedStyles: styles.productInformationStyles,
                    }}
                />
                {isTabletScreen && (
                    <ProductListProductInformation
                        showBrand={false}
                        showTitle={false}
                        showPartNumbers={false}
                        showAvailability={props.showAvailability && !props.product.isVariantParent}
                        showAttributes={false}
                        showImageBadges={false}
                        showTextBadges={false}
                        {...{
                            showPrice: props.showPrice,
                            extendedStyles: styles.productInformationStyles,
                        }}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actionsGridItem}>
                <ProductPriceAndActions
                    showAddToList={props.showAddToList}
                    showPrice={!isTabletScreen && props.showPrice}
                    showAddToCartActions={true}
                    showAvailability={!isTabletScreen && props.showAvailability && !props.product.isVariantParent}
                    showCompare={props.showCompare}
                    extendedStyles={styles.productPriceAndActionsStyles}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.searchScoreDataGridItem}>
                <ProductSearchScoreData />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
